import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { T } from './UI';

const PHONE = '+353 85 123 4567';
const WA    = '353851234567';

export default function Navbar() {
  const location  = useLocation();
  const { isAdmin } = useAdmin();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  useEffect(() => setMobileOpen(false), [location]);

  const isHome    = location.pathname === '/';
  const onDark    = (isHome && !scrolled && !mobileOpen);
  const navBg     = onDark ? 'transparent' : T.bg;
  const navInk    = onDark ? T.bg          : T.ink;
  const navBorder = onDark ? 'transparent' : T.surface;

  const links = [
    { label: 'Stock',    to: '/stock'   },
    { label: 'Import',   to: '/import'  },
    { label: 'About',    to: '/about'   },
    { label: 'Contact',  to: '/contact' },
  ];
  const active = (to) => location.pathname === to || (to === '/stock' && location.pathname.startsWith('/car'));

  return (
    <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, background: navBg, borderBottom: `1px solid ${navBorder}`, transition: 'background 0.3s, border-color 0.3s' }}>
      <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)', display: 'flex', alignItems: 'center', height: 68, gap: 32 }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, textDecoration: 'none' }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <rect width="28" height="28" fill={T.accent}/>
            <text x="14" y="19" textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="Playfair Display,serif" fill="#fff">NA</text>
          </svg>
          <span style={{ color: navInk, fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em', transition: 'color 0.3s' }}>
            Northbridge
          </span>
        </Link>

        {/* Desktop nav */}
        <nav style={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
          {links.map(l => (
            <Link key={l.to} to={l.to}
              style={{ color: active(l.to) ? T.accent : navInk, fontSize: 'var(--step-sm)', fontWeight: 500, padding: '5px 12px', letterSpacing: '0.02em', transition: 'color 0.2s', borderBottom: active(l.to) ? `1px solid ${T.accent}` : '1px solid transparent' }}
              onMouseEnter={e => e.currentTarget.style.color = T.accent}
              onMouseLeave={e => e.currentTarget.style.color = active(l.to) ? T.accent : navInk}>
              {l.label}
            </Link>
          ))}
          {isAdmin && (
            <Link to="/admin"
              style={{ color: T.muted, fontSize: 'var(--step-xs)', fontWeight: 500, padding: '5px 12px', letterSpacing: '0.04em', textTransform: 'uppercase', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = T.accent}
              onMouseLeave={e => e.currentTarget.style.color = T.muted}>
              Admin
            </Link>
          )}
        </nav>

        {/* Right */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <a href={`https://wa.me/${WA}`} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#25D366', color: '#fff', padding: '8px 16px', fontSize: 'var(--step-xs)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', textDecoration: 'none', transition: 'opacity 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.855L.054 23.946l6.283-1.649A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm5.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
            WhatsApp
          </a>
          <a href={`tel:${PHONE}`}
            style={{ color: navInk, fontSize: 'var(--step-xs)', fontWeight: 500, letterSpacing: '0.02em', transition: 'color 0.2s', display: 'none' }}
            className="nav-phone"
            onMouseEnter={e => e.currentTarget.style.color = T.accent}
            onMouseLeave={e => e.currentTarget.style.color = navInk}>
            {PHONE}
          </a>

          {/* Mobile hamburger */}
          <button onClick={() => setMobileOpen(o => !o)}
            style={{ background: 'none', border: 'none', color: navInk, fontSize: 22, cursor: 'pointer', display: 'none', padding: 4, lineHeight: 1 }}
            className="mobile-burger">
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div style={{ background: T.ink, borderTop: `1px solid ${T.muted}`, padding: '20px var(--margin) 28px' }}>
          {links.map(l => (
            <Link key={l.to} to={l.to}
              style={{ display: 'block', color: active(l.to) ? T.accent : T.bg, padding: '13px 0', fontSize: 'var(--step-lead)', fontFamily: 'Playfair Display, serif', fontWeight: 400, borderBottom: `1px solid rgba(255,255,255,0.08)`, letterSpacing: '-0.01em' }}>
              {l.label}
            </Link>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <a href={`https://wa.me/${WA}`} target="_blank" rel="noreferrer"
              style={{ flex: 1, background: '#25D366', color: '#fff', padding: '12px', fontSize: 'var(--step-xs)', fontWeight: 600, textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              WhatsApp
            </a>
            <a href={`tel:${PHONE}`}
              style={{ flex: 1, background: T.accent, color: T.accentInk, padding: '12px', fontSize: 'var(--step-xs)', fontWeight: 600, textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Call Us
            </a>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 860px) {
          .mobile-burger { display: flex !important; }
          nav { display: none !important; }
          .nav-phone { display: none !important; }
        }
        @media (min-width: 1100px) {
          .nav-phone { display: flex !important; }
        }
      `}</style>
    </header>
  );
}
